/**
 * Changement de l'adresse e-mail du compte (« Change email » de Bitwarden, Proton Pass, 1Password).
 *
 * L'e-mail n'est pas une simple etiquette : c'est le SEL de la derivation Argon2id (`docs/ARCHITECTURE.md` §1).
 * Le changer revient donc a re-sceller la `vaultKey` sous la derivation (meme mot de passe maitre, nouvel e-mail) :
 * `changeMasterPasswordAsync` avec `newEmail`, le chemin deja eprouve du changement de mot de passe. Aucune entree
 * n'est rechiffree, aucune revision ne bouge.
 *
 * Ordre volontaire, calque sur `master-password-change.ts` :
 *  1. re-scellement LOCAL -- un mot de passe maitre faux echoue ici, avant tout appel reseau ;
 *  2. si l'appareil est connecte : le serveur recoit la nouvelle adresse, la preuve actuelle, la preuve nouvelle
 *     et l'enveloppe re-scellee (jamais un mot de passe, jamais la `vaultKey`), revoque toutes les sessions et
 *     rend une session neuve, reprise ici ;
 *  3. seulement ensuite l'enveloppe locale est persistee : un refus du serveur (adresse prise, preuve fausse)
 *     laisse l'appareil exactement dans son etat d'avant -- jamais une enveloppe locale scellee sous une adresse
 *     que le serveur ne connait pas.
 *
 * La cle de recuperation et les passkeys de deverrouillage restent valables : elles scellent la `vaultKey`, pas la
 * derivation du mot de passe maitre.
 */
import { DecryptionFailedError, InvalidParameterError, changeMasterPasswordAsync, normalizeEmail } from '@klavox/crypto-core';
import type { VaultRepository } from './repository.js';
import { KlavoxApiError, changeServerEmail } from './sync-client.js';
import type { AccountEnvelope, RemoteSession } from './types.js';

/** Meme forme que le serveur (`apps/server/src/validation.ts`) : refusee ici, rien ne part. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;
/** Borne d'une adresse e-mail (RFC 5321 : 254 caracteres pour un chemin complet). */
const MAX_EMAIL_LENGTH = 254;

export class InvalidNewEmailError extends Error {}
export class SameEmailError extends Error {}

/**
 * Nouvelle adresse normalisee comme le sel de derivation (`normalizeEmail` : NFC, espaces, minuscules), ou refus.
 * Une adresse identique a l'actuelle APRES normalisation est refusee : re-sceller pour rien deconnecterait tous
 * les autres appareils.
 */
export function parseNewAccountEmail(raw: string, currentEmail: string): string {
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  if (trimmed.length === 0 || trimmed.length > MAX_EMAIL_LENGTH || !EMAIL_SHAPE.test(trimmed)) {
    throw new InvalidNewEmailError('Adresse e-mail invalide : rien n’a ete modifie.');
  }
  const email = normalizeEmail(trimmed);
  // Coffre cree sans e-mail : aucune adresse actuelle a comparer.
  if (currentEmail.trim() !== '' && email === normalizeEmail(currentEmail)) {
    throw new SameEmailError('C’est deja l’adresse de ce compte : rien n’a ete modifie.');
  }
  return email;
}

export interface ChangeAccountEmailInput {
  readonly repository: VaultRepository;
  readonly envelope: AccountEnvelope;
  readonly masterPassword: string;
  readonly newEmail: string;
  /** Session serveur en cours ; `null` en mode local pur (l'adresse ne sert alors que de sel local). */
  readonly remoteSession: RemoteSession | null;
}

export interface ChangedAccountEmail {
  readonly envelope: AccountEnvelope;
  /** Session renouvelee par le serveur (les precedentes sont revoquees) ; `null` en mode local. */
  readonly remoteSession: RemoteSession | null;
  /** Vrai si le serveur a ete mis a jour : les autres appareils sont deconnectes. */
  readonly serverUpdated: boolean;
}

export async function changeAccountEmail(input: ChangeAccountEmailInput): Promise<ChangedAccountEmail> {
  const newEmail = parseNewAccountEmail(input.newEmail, input.envelope.email);

  const changed = await changeMasterPasswordAsync({
    email: input.envelope.email,
    currentMasterPassword: input.masterPassword,
    newMasterPassword: input.masterPassword,
    newEmail,
    kdfParams: input.envelope.kdfParams,
    protectedSymmetricKey: input.envelope.protectedSymmetricKey,
  });

  let remoteSession = input.remoteSession;
  if (remoteSession !== null) {
    // La preuve ACTUELLE est exigee en plus de la session : une session volee ne suffit pas a detourner le compte.
    const session = await changeServerEmail(remoteSession.baseUrl, remoteSession.sessionToken, {
      newEmail,
      currentMasterPasswordHashBase64: changed.currentMasterPasswordHashBase64,
      newMasterPasswordHashBase64: changed.masterPasswordHashBase64,
      newProtectedSymmetricKey: changed.protectedSymmetricKey,
      newKdfParams: changed.kdfParams,
    });
    remoteSession = {
      ...remoteSession,
      sessionToken: session.sessionToken,
      refreshToken: session.refreshToken,
      expiresAt: session.expiresAt,
    };
  }

  const envelope: AccountEnvelope = {
    ...input.envelope,
    email: changed.email,
    kdfParams: changed.kdfParams,
    protectedSymmetricKey: changed.protectedSymmetricKey,
  };
  await input.repository.saveAccountEnvelope(envelope);
  return { envelope, remoteSession, serverUpdated: input.remoteSession !== null };
}

/** Message utilisateur sans detail technique : dit toujours ce qui a ete modifie, ou non. */
export function describeEmailChangeError(cause: unknown): string {
  if (cause instanceof InvalidNewEmailError || cause instanceof SameEmailError) return cause.message;
  if (cause instanceof DecryptionFailedError) return 'Mot de passe maitre incorrect : rien n’a ete modifie.';
  if (cause instanceof InvalidParameterError) return 'Adresse e-mail refusee : rien n’a ete modifie.';
  if (cause instanceof KlavoxApiError) {
    if (cause.code === 'account_already_exists') return 'Cette adresse est deja celle d’un autre compte : rien n’a ete modifie.';
    if (cause.code === 'authentication_failed') return 'Mot de passe maitre refuse par le serveur : rien n’a ete modifie.';
    if (cause.status === 429) return 'Trop d’essais : patientez quelques minutes. Rien n’a ete modifie.';
    if (cause.code === 'session_expired') return 'Session serveur expiree : reconnectez la synchro, puis recommencez. Rien n’a ete modifie.';
    if (cause.code === 'network_error') return 'Serveur injoignable : rien n’a ete modifie, ni ici ni sur le serveur.';
  }
  return cause instanceof Error ? `Echec, rien n’a ete modifie : ${cause.message}` : 'Echec du changement d’adresse e-mail';
}
